import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showFundraiseDropdown, setShowFundraiseDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  const [user, setUser] = useState(null);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Determine active tab based on current route
  useEffect(() => {
    const path = location.pathname;

    if (path === "/" || path.startsWith("/campaign")) {
      setActiveTab("home");
    } else if (path === "/about") {
      setActiveTab("about");
    } else if (path === "/contact") {
      setActiveTab("contact");
    } else if (path === "/start-fundraiser") {
      setActiveTab("fundraise");
    } else if (path === "/signin") {
      setActiveTab("signin");
    } else {
      setActiveTab("");
    }
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close all dropdowns when route changes
  useEffect(() => {
    setShowFundraiseDropdown(false);
  }, [location]);

  // Detect logged in user from sessionStorage
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      setUser(null);
    }
  }, [location]);

  const navItems = [
    {
      name: "Home",
      path: "/",
      tabKey: "home",
    },
    {
      name: "About",
      path: "/about",
      tabKey: "about",
    },
    {
      name: "Contact",
      path: "/contact",
      tabKey: "contact",
    },
  ];

  // Purpose options
  const purposeOptions = [
    { value: "medical", label: "Medical", icon: "fa-heart-pulse", color: "from-rose-500 to-red-500" },
    { value: "education", label: "Education", icon: "fa-graduation-cap", color: "from-blue-500 to-indigo-500" },
    { value: "emergency", label: "Emergency", icon: "fa-bolt", color: "from-amber-500 to-orange-500" },
    { value: "animals", label: "Animals", icon: "fa-paw", color: "from-teal-500 to-emerald-500" },
    { value: "business", label: "Business", icon: "fa-briefcase", color: "from-emerald-500 to-teal-500" },
    { value: "community", label: "Community", icon: "fa-users", color: "from-purple-500 to-pink-500" },
    { value: "creative", label: "Creative", icon: "fa-paint-brush", color: "from-sky-500 to-cyan-500" },
    { value: "faith", label: "Faith", icon: "fa-church", color: "from-rose-500 to-pink-500" },
  ];

  const handlePurposeSelect = (purpose) => {
    localStorage.setItem("preselectedPurpose", purpose.value);
    navigate("/start-fundraiser");
    setShowFundraiseDropdown(false);
    setMenuOpen(false);
  };

  const handleGeneralFundraiser = () => {
    localStorage.removeItem("preselectedPurpose");
    navigate("/start-fundraiser");
    setShowFundraiseDropdown(false);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    localStorage.removeItem("completeUserData");
    setUser(null);
    setProfileSheetOpen(false);
    navigate("/");
  };

  // Store user data for Step1 when starting fundraiser
  const handleStartFundraiser = () => {
    if (user) {
      localStorage.setItem('completeUserData', JSON.stringify({
        name: user.name,
        email: user.email,
        mobile: user.mobile || '',
        userId: user.id || user.userId
      }));
    }
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setShowFundraiseDropdown(false);
    setHoveredItem(null);
  };

  // Handle mouse leave from navbar area
  const handleNavbarMouseLeave = () => {
    setShowFundraiseDropdown(false);
    setHoveredItem(null);
  };

  // Check if tab is active
  const isTabActive = (tabKey) => {
    return activeTab === tabKey;
  };

  return (
    <nav
      className={`flex sticky top-0 z-50 items-center justify-between px-4 md:px-8 lg:px-12 py-3 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-white/20' 
          : 'bg-white/80 backdrop-blur-sm shadow-md'
      } w-full min-h-[70px]`}
      onMouseLeave={handleNavbarMouseLeave}
    >
      {/* Left Section: Logo + Navigation */}
      <div className="flex items-center gap-6 lg:gap-10">
        {/* Logo */}
        <Link to="/" onClick={closeAllDropdowns} className="relative group">
          <div className="absolute -inset-2 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 rounded-lg blur-lg opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
          <img
            src="/assets/image.jpg"
            alt="DonatePe"
            className="h-10 md:h-12 lg:h-14 w-auto relative transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1 lg:gap-2">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`relative px-4 py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 group ${
                isTabActive(item.tabKey)
                  ? "text-teal-700"
                  : "text-gray-600 hover:text-teal-600"
              }`}
              onMouseEnter={() => setHoveredItem(item.name)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={closeAllDropdowns}
            >
              {item.name}
              
              {/* Active indicator */}
              {isTabActive(item.tabKey) ? (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></span>
              ) : (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-0 group-hover:scale-100"></span>
              )}
            </Link>
          ))}
        </div>
      </div>

      {/* Right Section: Actions */}
      <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
        {/* Fundraise Button with Dropdown - Desktop */}
        <div className="hidden md:block relative">
          <button
            className={`relative group flex items-center gap-2 px-5 py-2.5 rounded-full text-sm lg:text-base font-semibold transition-all duration-300 ${
              isTabActive("fundraise")
                ? "bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg shadow-teal-200/50"
                : "bg-white text-gray-700 border-2 border-gray-200 hover:border-teal-600 hover:text-teal-600 hover:shadow-lg"
            }`}
            onMouseEnter={() => {
              setHoveredItem("fundraise");
              setShowFundraiseDropdown(true);
            }}
            onClick={() => setShowFundraiseDropdown(!showFundraiseDropdown)}
          >
            <span>Start Fundraiser</span>
            <svg 
              className={`w-4 h-4 transition-transform duration-300 ${showFundraiseDropdown ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
            
            {/* Glow effect on hover */}
            {!isTabActive("fundraise") && (
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            )}
          </button>

          {/* Fundraise Dropdown - Premium Design */}
          {showFundraiseDropdown && (
            <div 
              className="absolute right-0 mt-3 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-100/50 overflow-hidden z-50 animate-dropdown"
              onMouseEnter={() => setShowFundraiseDropdown(true)}
              onMouseLeave={() => setShowFundraiseDropdown(false)}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-4">
                <h4 className="text-white font-semibold text-left text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Start fundraising for
                </h4>
              </div>
              
              {/* Scrollable options */}
              <div className="p-2 max-h-[320px] overflow-y-auto">
                {purposeOptions.map((option) => (
                  <button
                    key={option.value}
                    className="w-full px-4 py-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-50 group flex items-center justify-between"
                    onClick={() => handlePurposeSelect(option)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${option.color} flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <i className={`fas ${option.icon}`}></i>
                      </div>
                      <span className="font-medium text-gray-700 group-hover:text-gray-900">
                        {option.label}
                      </span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
              
              {/* Footer with general fundraiser */}
              <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                <button
                  onClick={handleGeneralFundraiser}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl text-gray-700 font-medium hover:shadow-md transition-all duration-300 group border border-gray-200 hover:border-teal-600"
                >
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span className="font-semibold">General Fundraiser</span>
                  </span>
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">Quick start</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu - Desktop */}
        <div className="hidden md:flex items-center gap-2">
          {!user ? (
            <>
              {/* Sign In - Premium */}
              <Link
                to="/signin"
                className={`relative px-5 py-2.5 rounded-full text-sm lg:text-base font-medium transition-all duration-300 group ${
                  isTabActive("signin")
                    ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                    : "text-gray-600 hover:text-teal-600 border-2 border-transparent hover:border-teal-200 hover:bg-teal-50/50"
                }`}
                onClick={closeAllDropdowns}
              >
                Sign in
                {isTabActive("signin") && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                )}
              </Link>

              {/* Sign Up - Premium Gradient */}
              <Link
                to="/start-fundraiser"
                onClick={() => {
                  localStorage.removeItem("preselectedPurpose");
                  closeAllDropdowns();
                  handleStartFundraiser();
                }}
                className="relative group px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full text-sm lg:text-base font-semibold shadow-lg hover:shadow-xl hover:shadow-teal-200/50 transition-all duration-300 hover:scale-105"
              >
                <span className="relative z-10">Sign up</span>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 blur-lg opacity-40 group-hover:opacity-60 transition-opacity duration-500"></div>
              </Link>
            </>
          ) : (
            <>
              {/* Profile Icon */}
              <button
                onClick={() => setProfileSheetOpen(true)}
                className="relative w-11 h-11 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
              >
                <span className="text-lg font-bold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
                
                {/* Hover glow */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="relative flex md:hidden w-11 h-11 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 items-center justify-center group"
          onClick={() => {
            setMenuOpen(!menuOpen);
            closeAllDropdowns();
          }}
          aria-label="Toggle menu"
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative w-5 h-5 flex flex-col items-center justify-center gap-1.5">
            <span className={`block w-5 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`block w-5 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-5 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </div>
        </button>
      </div>

      {/* Mobile Menu - Premium Slide Down */}
      <div
        className={`absolute top-full left-0 right-0 md:hidden bg-white/95 backdrop-blur-xl flex flex-col shadow-2xl border-t border-gray-100/50 overflow-hidden transition-all duration-500 ease-out ${
          menuOpen
            ? "max-h-[600px] opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        <div className="p-5 space-y-3">
          {/* Mobile Navigation Links */}
          <div className="space-y-1">
            {[...navItems, { name: "Fundraise", path: "#", tabKey: "fundraise", isDropdown: true }].map((item, index) => (
              <div key={item.name}>
                {item.isDropdown ? (
                  <>
                    <button
                      className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-300 ${
                        isTabActive("fundraise")
                          ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 font-semibold border border-teal-200"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => setShowFundraiseDropdown(!showFundraiseDropdown)}
                    >
                      <span className="font-medium">Start Fundraiser</span>
                      <svg 
                        className={`w-4 h-4 transition-transform duration-300 ${showFundraiseDropdown ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Mobile Dropdown */}
                    {showFundraiseDropdown && (
                      <div className="mt-2 ml-2 space-y-1 animate-slideDown">
                        <div className="max-h-[240px] overflow-y-auto rounded-xl bg-gray-50/80 p-1">
                          {purposeOptions.map((option) => (
                            <button
                              key={option.value}
                              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white transition-all duration-300 text-gray-700 group"
                              onClick={() => handlePurposeSelect(option)}
                            >
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${option.color} flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300`}>
                                <i className={`fas ${option.icon}`}></i>
                              </div>
                              <span className="flex-1 text-left text-sm font-medium">{option.label}</span>
                              <svg className="w-4 h-4 text-gray-400 group-hover:text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          ))}
                        </div>
                        
                        <button
                          onClick={handleGeneralFundraiser}
                          className="w-full mt-2 flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-medium shadow-lg"
                        >
                          <span className="flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            <span>General Fundraiser</span>
                          </span>
                          <span className="text-xs bg-white/30 px-2 py-1 rounded-full">Quick</span>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => {
                      setMenuOpen(false);
                      closeAllDropdowns();
                    }}
                    className={`flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 ${
                      isTabActive(item.tabKey)
                        ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 font-semibold border border-teal-200"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>{item.name}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Auth Buttons */}
          <div className="pt-3 border-t border-gray-100">
            {!user ? (
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/signin"
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3.5 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium text-center hover:border-teal-600 hover:text-teal-600 transition-all duration-300"
                >
                  Sign in
                </Link>
                <Link
                  to="/start-fundraiser"
                  onClick={() => {
                    localStorage.removeItem("preselectedPurpose");
                    setMenuOpen(false);
                    handleStartFundraiser();
                  }}
                  className="px-4 py-3.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-medium text-center shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Sign up
                </Link>
              </div>
            ) : (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  setProfileSheetOpen(true);
                }}
                className="w-full flex items-center justify-between px-4 py-3.5 bg-teal-50 rounded-xl text-teal-700 font-semibold border border-teal-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span>{user?.name || 'My Profile'}</span>
                </div>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Profile Right Sheet - Premium Design */}
      {profileSheetOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 animate-fadeIn"
            onClick={() => setProfileSheetOpen(false)}
          ></div>

          <div className="fixed top-0 right-0 h-full w-[400px] max-w-[90%] bg-white/95 backdrop-blur-xl z-50 shadow-2xl rounded-l-3xl overflow-hidden animate-slideInRight border-l border-gray-100/50">
            
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center text-white text-2xl font-bold shadow-xl">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl font-bold">{user?.name || 'User'}</h3>
                    <p className="text-sm text-white/80">{user?.email || ''}</p>
                  </div>
                </div>
                <button
                  onClick={() => setProfileSheetOpen(false)}
                  className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-3 p-6">
              {[
                { label: 'Campaigns', value: '0', icon: 'fa-flag' },
                { label: 'Donations', value: '0', icon: 'fa-hand-holding-heart' },
                { label: 'Impact', value: '0', icon: 'fa-users' }
              ].map((stat, i) => (
                <div key={i} className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 text-center border border-gray-100/50">
                  <div className="w-8 h-8 mx-auto bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center text-white text-sm mb-2">
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">{stat.value}</h2>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Menu Items */}
            <div className="px-6 space-y-2">
              {[
                { label: "My Contributions", icon: "fa-hand-holding-heart", path: "/my-contributions", color: "from-teal-500 to-emerald-500" },
                { label: "My Fundraisers", icon: "fa-flag", path: "/my-fundraiser", color: "from-amber-500 to-orange-500" },
                { label: "Profile Settings", icon: "fa-user-cog", path: "/settings", color: "from-blue-500 to-indigo-500" },
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full flex items-center justify-between px-4 py-3.5 bg-gray-50/80 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
                  onClick={() => {
                    setProfileSheetOpen(false);
                    navigate(item.path);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`fas ${item.icon}`}></i>
                    </div>
                    <span className="font-medium text-gray-700">{item.label}</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}

              {/* Logout Button */}
              <button
                className="w-full flex items-center justify-between px-4 py-3.5 bg-rose-50/80 hover:bg-rose-100 rounded-xl transition-all duration-300 group mt-4"
                onClick={handleLogout}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-rose-500 to-red-500 flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300">
                    <i className="fas fa-sign-out-alt"></i>
                  </div>
                  <span className="font-medium text-rose-600">Logout</span>
                </div>
                <svg className="w-5 h-5 text-rose-400 group-hover:text-rose-600 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
              <p className="text-xs text-gray-400">
                DonatePe • Making giving beautiful
              </p>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes dropdown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideInRight {
          animation: slideInRight 0.3s cubic-bezier(0.2, 0.9, 0.3, 1);
        }

        .animate-dropdown {
          animation: dropdown 0.3s cubic-bezier(0.2, 0.9, 0.3, 1);
        }
      `}</style>
    </nav>
  );
}